import React from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './components/ui';
import Layout from './components/Layout';
import BackButtonHandler from './components/BackButtonHandler';
import BackKeyHandler from './components/BackKeyHandler';
import DeviceGuard from './components/DeviceGuard';
import Login from './pages/login';
import Register from './pages/register';
import OTP from './pages/otp';
import ChangePassword from './pages/changePassword';
import Invite from './pages/invite';
import Dashboard from './pages/dashboard';
import Events from './pages/events';
import Users from './pages/users';
import Functions from './pages/functions';
import Members from './pages/members';
import Setup from './pages/setup';
import Profile from './pages/profile';
import Status from './pages/status';
import Headcount from './pages/headcount';
import Reminders from './pages/reminders';
import Services from './pages/services';
import ServiceDetail from './pages/serviceDetail';
import MyWork from './pages/myWork';
import Cards from './pages/cards';
import InviteForms from './pages/inviteForms';
import UserManagement from './pages/userManagement';

function Protected({ children }) {
  const { user, ready, mustChangePassword } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/" replace />;
  // Default password '1234' → nothing works until it is changed (all platforms).
  if (mustChangePassword) return <Navigate to="/change-password" replace />;
  return children;
}

// /change-password itself: signed in, but NOT blocked by the must-change rule.
function SignedIn({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

// Auth pages (Login / OTP / Register): once a session is saved in localStorage,
// never show these again — go straight to the app. This is what stops the back
// button (or a re-open) from landing on the OTP/login screen while signed in.
function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

// Plain users land on Members (their only screen); admins land on the Dashboard.
function Home() {
  const { isAdmin } = useAuth();
  return <Navigate to={isAdmin ? 'dashboard' : 'members'} replace />;
}

function Access({ adminOnly = false, superAdminOnly = false, children }) {
  const { isAdmin, isSuperAdmin } = useAuth();
  const allowed = superAdminOnly ? isSuperAdmin : adminOnly ? isAdmin : true;
  if (!allowed) return <Navigate to="/app" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <HashRouter>
          <BackButtonHandler />
          <BackKeyHandler />
          <DeviceGuard />
          <Routes>
            <Route path="/" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/otp" element={<PublicOnly><OTP /></PublicOnly>} />
            <Route path="/change-password" element={<SignedIn><ChangePassword /></SignedIn>} />
            <Route path="/invite" element={<Invite />} />
            <Route path="/app" element={<Protected><Layout /></Protected>}>
              <Route index element={<Home />} />
              <Route path="dashboard" element={<Access adminOnly><Dashboard /></Access>} />
              <Route path="events" element={<Access adminOnly><Events /></Access>} />
              <Route path="setup" element={<Access adminOnly><Setup /></Access>} />
              <Route path="functions" element={<Access adminOnly><Functions /></Access>} />
              <Route path="members" element={<Members />} />
              <Route path="invite-forms" element={<InviteForms />} />
              <Route path="status" element={<Status />} />
              <Route path="reminders" element={<Reminders />} />
              <Route path="services" element={<Services />} />
              <Route path="services/:serviceId" element={<ServiceDetail />} />
              <Route path="my-work" element={<MyWork />} />
              <Route path="cards" element={<Cards />} />
              <Route path="headcount" element={<Access adminOnly><Headcount /></Access>} />
              <Route path="users" element={<Users />} />
              <Route path="user-management" element={<Access superAdminOnly><UserManagement /></Access>} />
              <Route path="profile" element={<Profile />} />
              <Route path="guests" element={<Navigate to="/app/members" replace />} />
              <Route path="arrangements" element={<Navigate to="/app" replace />} />
              <Route path="team" element={<Navigate to="/app" replace />} />
              <Route path="vendors" element={<Navigate to="/app" replace />} />
              <Route path="payments" element={<Navigate to="/app" replace />} />
              <Route path="tasks" element={<Navigate to="/app" replace />} />
              <Route path="rsvp" element={<Navigate to="/app" replace />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </HashRouter>
      </ToastProvider>
    </AuthProvider>
  );
}
